import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, Volume2, VolumeX, SkipForward, SkipBack, Link, X } from 'lucide-react';
import contentService from '../../services/contentService';
import MusicPlaylistService from '../../services/musicPlaylistService';
import { API_BASE_URL, BUSINESS_ID } from '../../config/api';

const PLAYLIST_STORAGE_KEY = 'rainbow_sails_playlist';
const ACTIVE_PLAYLIST_KEY = 'rainbow_sails_active_playlist_id';
const PLAYER_STATE_STORAGE_KEY = 'rainbow_sails_player_state';
const PLAYER_UI_STORAGE_KEY = 'rainbow_sails_player_ui';
const PLAYER_AUTOPLAY_KEY = 'rainbow_sails_autoplay_timestamp';
const AUTOPLAY_COOLDOWN_MS = 24 * 60 * 60 * 1000;
const MUSIC_KEYWORDS = ['music', 'album', 'track', 'song', 'audio', 'soundtrack'];
const AUDIO_EXTENSIONS = /\.(mp3|wav|m4a|aac|ogg|flac)$/i;

const getStoredPlayerUiState = () => {
  if (typeof window === 'undefined') return { isMinimized: true };
  try {
    const raw = window.localStorage.getItem(PLAYER_UI_STORAGE_KEY);
    return raw ? JSON.parse(raw) : { isMinimized: true };
  } catch (error) {
    console.warn('🎵 Failed to read Zee player UI state:', error);
    return { isMinimized: true };
  }
};

const getLastAutoPlayTimestamp = () => {
  if (typeof window === 'undefined') return null;
  try {
    const value = window.localStorage.getItem(PLAYER_AUTOPLAY_KEY);
    if (!value) return null;
    const timestamp = Number(value);
    return Number.isFinite(timestamp) ? timestamp : null;
  } catch (error) {
    console.warn('🎵 Failed to read Zee autoplay timestamp:', error);
    return null;
  }
};

const setLastAutoPlayTimestamp = (timestamp) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PLAYER_AUTOPLAY_KEY, String(timestamp));
  } catch (error) {
    console.warn('🎵 Failed to persist Zee autoplay timestamp:', error);
  }
};

const isAutoPlayAllowed = () => {
  const lastTimestamp = getLastAutoPlayTimestamp();
  if (!lastTimestamp) {
    return true;
  }
  return Date.now() - lastTimestamp >= AUTOPLAY_COOLDOWN_MS;
};

const clampVolume = (value) => {
  if (Number.isNaN(value)) return 0;
  return Math.min(Math.max(value, 0), 1);
};

const resolveAbsoluteUrl = (url, folder, origin) => {
  if (!url) return null;

  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  if (url.startsWith('//')) {
    const protocol = origin?.startsWith('https') ? 'https:' : 'http:';
    return `${protocol}${url}`;
  }

  // Handle backend upload paths (/uploads/content/audio/...)
  if (url.startsWith('/uploads/')) {
    // If the URL is already absolute (includes origin), use it as-is
    // Otherwise, construct the backend URL
    // The contentService should have already converted this, but handle it here too
    if (origin) {
      // Check if we need to use API base URL for uploads
      // For now, assume uploads are served from the same origin or API base
      return `${origin}${url}`;
    }
    return url;
  }

  if (url.startsWith('/')) {
    return origin ? `${origin}${url}` : url;
  }

  const basePath = folder ? `/content/${folder}/` : '/content/';
  return origin ? `${origin}${basePath}${url}` : `${basePath}${url}`;
};

const extractCandidateUrl = (candidate = {}) =>
  candidate.url ||
  candidate.href ||
  candidate.src ||
  candidate.path ||
  candidate.liveUrl ||
  candidate.downloadUrl;

const collectAudioSourcesFromEntry = (entry, origin) => {
  if (!entry) return [];

  const folder = entry.folder || entry.id || '';
  const sources = new Set();

  const addSource = (value) => {
    if (!value) return;
    const resolved = resolveAbsoluteUrl(value, folder, origin);
    if (resolved) {
      console.log('🎵 [COLLECT-AUDIO] Adding audio source:', resolved);
      sources.add(resolved);
    } else {
      console.log('🎵 [COLLECT-AUDIO] Failed to resolve audio source:', value);
    }
  };

  const consider = (candidate) => {
    if (!candidate) return;

    if (typeof candidate === 'string') {
      if (AUDIO_EXTENSIONS.test(candidate) || candidate.startsWith('/content/') || candidate.startsWith('content/') || candidate.startsWith('/uploads/')) {
        addSource(candidate);
      }
      return;
    }

    if (typeof candidate === 'object') {
      const possibleUrl = extractCandidateUrl(candidate);
      if (!possibleUrl) return;

      const typeHint = (candidate.type || '').toLowerCase();
      const nameHint = candidate.name || candidate.title || candidate.label || '';

      if (
        typeHint === 'audio' ||
        AUDIO_EXTENSIONS.test(possibleUrl) ||
        AUDIO_EXTENSIONS.test(nameHint) ||
        possibleUrl.startsWith('/content/') ||
        possibleUrl.startsWith('content/') ||
        possibleUrl.startsWith('/uploads/')
      ) {
        addSource(possibleUrl);
      }
    }
  };

  // Check tags.audioUrl first (this is where the backend stores uploaded audio)
  if (entry.tags) {
    console.log('🎵 [COLLECT-AUDIO] Entry has tags:', {
      tagsType: typeof entry.tags,
      tagsValue: entry.tags,
      hasAudioUrl: !!(entry.tags && typeof entry.tags === 'object' && entry.tags.audioUrl)
    });
    
    if (typeof entry.tags === 'object' && entry.tags.audioUrl) {
      console.log('🎵 [COLLECT-AUDIO] Found audioUrl in tags:', entry.tags.audioUrl);
      addSource(entry.tags.audioUrl);
    } else if (typeof entry.tags === 'string') {
      // Try to parse if it's a JSON string
      try {
        const parsedTags = JSON.parse(entry.tags);
        if (parsedTags && parsedTags.audioUrl) {
          console.log('🎵 [COLLECT-AUDIO] Found audioUrl in parsed tags:', parsedTags.audioUrl);
          addSource(parsedTags.audioUrl);
        }
      } catch (e) {
        console.log('🎵 [COLLECT-AUDIO] Tags is a string but not valid JSON:', e);
      }
    }
  } else {
    console.log('🎵 [COLLECT-AUDIO] Entry has no tags field');
  }

  if (Array.isArray(entry.audio)) {
    entry.audio.forEach(consider);
  }

  if (Array.isArray(entry.tracks)) {
    entry.tracks.forEach(consider);
  }

  if (Array.isArray(entry.music)) {
    entry.music.forEach(consider);
  }

  if (Array.isArray(entry.audios)) {
    entry.audios.forEach(consider);
  }

  if (Array.isArray(entry.media)) {
    entry.media.forEach(consider);
  }

  if (entry.primaryMediaType === 'audio' && entry.primaryMediaUrl) {
    addSource(entry.primaryMediaUrl);
  }

  if (entry.assets && typeof entry.assets === 'object') {
    Object.values(entry.assets).forEach(consider);
  }

  if (entry.rawContent) {
    const raw = entry.rawContent;
    if (Array.isArray(raw.audio)) {
      raw.audio.forEach(consider);
    }
    if (Array.isArray(raw.audios)) {
      raw.audios.forEach(consider);
    }
    if (Array.isArray(raw.media)) {
      raw.media.forEach(consider);
    }
    if (Array.isArray(raw.tracks)) {
      raw.tracks.forEach(consider);
    }
    if (Array.isArray(raw.music)) {
      raw.music.forEach(consider);
    }
    if (Array.isArray(raw.audioFiles)) {
      raw.audioFiles.forEach(consider);
    }
  }

  return Array.from(sources);
};

const stringContainsMusicKeyword = (value = '') => {
  if (typeof value !== 'string') return false;
  const lower = value.toLowerCase();
  return MUSIC_KEYWORDS.some((keyword) => lower.includes(keyword));
};

const arrayContainsMusicKeyword = (values = []) => {
  if (!Array.isArray(values)) return false;
  return values.some((value) => stringContainsMusicKeyword(value));
};

const isMusicContentEntry = (entry = {}) => {
  // Check if contentType is Audio with Music-related subcategory (new category system)
  // Note: "Music", "Music track", "Song", "Album track" are all treated the same
  if (entry.contentType && entry.contentType.toLowerCase() === 'audio') {
    const subcategory = entry.contentSubcategory ? entry.contentSubcategory.toLowerCase() : '';
    const musicSubcategories = ['music', 'music track', 'song', 'album track'];
    if (musicSubcategories.includes(subcategory)) {
      console.log('🎵 [IS-MUSIC] Entry is music (contentType === audio, subcategory ===', entry.contentSubcategory, '):', entry.title || entry.name);
      return true;
    }
  }

  // Check if contentType is explicitly 'music' (backwards compatibility)
  if (entry.contentType && entry.contentType.toLowerCase() === 'music') {
    console.log('🎵 [IS-MUSIC] Entry is music (contentType === music):', entry.title || entry.name);
    return true;
  }

  // Check if tags.audioUrl exists (content with uploaded audio)
  if (entry.tags) {
    let tagsObj = entry.tags;
    
    // Handle tags as JSON string (from localStorage)
    if (typeof entry.tags === 'string') {
      try {
        tagsObj = JSON.parse(entry.tags);
      } catch (e) {
        // Not valid JSON, skip
      }
    }
    
    if (tagsObj && typeof tagsObj === 'object' && tagsObj.audioUrl) {
      console.log('🎵 [IS-MUSIC] Entry is music (has tags.audioUrl):', entry.title || entry.name, tagsObj.audioUrl);
      return true;
    }
  }

  if (entry.primaryMediaType && entry.primaryMediaType.toLowerCase() === 'audio') {
    console.log('🎵 [IS-MUSIC] Entry is music (primaryMediaType === audio):', entry.title || entry.name);
    return true;
  }

  const candidateStrings = [
    entry.contentType,
    entry.type,
    entry.category,
    entry.subcategory,
    entry.collection,
    entry.folder,
    entry.title,
    entry.name,
    entry.displayName
  ];

  if (candidateStrings.some(stringContainsMusicKeyword)) {
    console.log('🎵 [IS-MUSIC] Entry is music (contains music keyword):', entry.title || entry.name);
    return true;
  }

  const candidateArrays = [
    entry.contentTypes,
    entry.categories,
    entry.labels,
    entry.collections
  ];

  if (candidateArrays.some(arrayContainsMusicKeyword)) {
    console.log('🎵 [IS-MUSIC] Entry is music (array contains music keyword):', entry.title || entry.name);
    return true;
  }

  const raw = entry.rawContent || {};

  if (typeof raw === 'object' && raw !== null) {
    const rawCandidateStrings = [
      raw.contentType,
      raw.type,
      raw.category,
      raw.subcategory,
      raw.collection,
      raw.title,
      raw.name,
      raw.displayName,
      raw.mediaType
    ];

    if (rawCandidateStrings.some(stringContainsMusicKeyword)) {
      return true;
    }

    const rawCandidateArrays = [raw.contentTypes, raw.categories, raw.labels];
    if (rawCandidateArrays.some(arrayContainsMusicKeyword)) {
      return true;
    }

    if (raw.primaryMediaType && raw.primaryMediaType.toLowerCase() === 'audio') {
      return true;
    }
  }

  console.log('🎵 [IS-MUSIC] Entry is NOT music:', entry.title || entry.name || entry.id);
  return false;
};

const getNavigationType = () => {
  if (typeof window === 'undefined' || typeof window.performance === 'undefined') {
    return 'navigate';
  }

  try {
    if (typeof window.performance.getEntriesByType === 'function') {
      const [entry] = window.performance.getEntriesByType('navigation') || [];
      if (entry && entry.type) {
        return entry.type;
      }
    }

    if (window.performance.navigation) {
      const { navigation } = window.performance;
      switch (navigation.type) {
        case navigation.TYPE_RELOAD:
          return 'reload';
        case navigation.TYPE_BACK_FORWARD:
          return 'back_forward';
        default:
          return 'navigate';
      }
    }
  } catch (error) {
    console.warn('🎵 Failed to detect Zee navigation type:', error);
  }

  return 'navigate';
};

const buildPlaylistFromContent = (content = []) => {
  if (!Array.isArray(content)) {
    console.log('🎵 [BUILD-PLAYLIST] Content is not an array:', typeof content);
    return [];
  }

  console.log('🎵 [BUILD-PLAYLIST] Processing', content.length, 'content items');
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const tracks = [];

  content.forEach((entry, entryIndex) => {
    if (!entry) {
      console.log(`🎵 [BUILD-PLAYLIST] Entry ${entryIndex} is null/undefined, skipping`);
      return;
    }

    const isMusic = isMusicContentEntry(entry);
    
    // Parse tags to check manager settings
    let tagsObj = {};
    if (entry.tags) {
      try {
        tagsObj = typeof entry.tags === 'string' ? JSON.parse(entry.tags) : entry.tags;
        if (typeof tagsObj !== 'object' || tagsObj === null) tagsObj = {};
      } catch (e) {
        tagsObj = {};
      }
    }

    // Check if track is enabled in player manager
    // Default to enabled (true) if showInPlayer is not explicitly set to false
    const isEnabled = tagsObj.showInPlayer !== false;
    
    console.log(`🎵 [BUILD-PLAYLIST] Entry ${entryIndex} (${entry.title || entry.name || entry.id}):`, {
      contentType: entry.contentType,
      contentSubcategory: entry.contentSubcategory,
      hasTags: !!entry.tags,
      hasAudioUrl: !!(tagsObj.audioUrl),
      isMusicContent: isMusic,
      isEnabled: isEnabled,
      playlistOrder: tagsObj.playlistOrder
    });

    // Skip if not music or not enabled in player manager
    if (!isMusic || !isEnabled) {
      if (isMusic && !isEnabled) {
        console.log(`🎵 [BUILD-PLAYLIST] Entry ${entryIndex} is disabled in player manager, skipping`);
      }
      return;
    }

    const audioSources = collectAudioSourcesFromEntry(entry, origin);
    console.log(`🎵 [BUILD-PLAYLIST] Entry ${entryIndex} audio sources:`, audioSources);
    
    if (!audioSources.length) {
      console.log(`🎵 [BUILD-PLAYLIST] Entry ${entryIndex} has no audio sources, skipping`);
      return;
    }

    const baseId = entry.id || entry.folder || `zee-track-${entryIndex}`;
    const baseTitle = entry.name || entry.title || entry.displayName || `Zee Track ${tracks.length + 1}`;
    const artist =
      entry.artist ||
      entry.musician ||
      entry.creator ||
      (entry.rawContent &&
        (entry.rawContent.artist || entry.rawContent.musician || entry.rawContent.creator)) ||
      'Zee';

    audioSources.forEach((src, srcIndex) => {
      const trackId = audioSources.length > 1 ? `${baseId}-audio-${srcIndex}` : `${baseId}-audio`;
      const trackTitle = audioSources.length > 1 ? `${baseTitle} (${srcIndex + 1})` : baseTitle;

      // Extract links from entry
      const websiteLink = entry.websiteLink || entry.url || (entry.slug ? `${origin}/content/${entry.slug}` : null);
      const streamingLinks = entry.streamingLinks || entry.rawContent?.streamingLinks || {};
      const extraLinks = entry.extraLinks || entry.rawContent?.extraLinks || [];
      
      // Build links object
      const links = {
        website: websiteLink,
        spotify: streamingLinks.spotify || null,
        appleMusic: streamingLinks.appleMusic || null,
        youtube: streamingLinks.youtubeMusic || streamingLinks.youtube || null,
        amazonMusic: streamingLinks.amazonMusic || null,
        deezer: streamingLinks.deezer || null,
        // Add any extra links that match known platforms
        ...extraLinks.reduce((acc, link) => {
          if (link.url && link.label) {
            const labelLower = link.label.toLowerCase();
            if (labelLower.includes('spotify') && !acc.spotify) acc.spotify = link.url;
            else if ((labelLower.includes('apple') || labelLower.includes('itunes')) && !acc.appleMusic) acc.appleMusic = link.url;
            else if (labelLower.includes('youtube') && !acc.youtube) acc.youtube = link.url;
            else if (labelLower.includes('amazon') && !acc.amazonMusic) acc.amazonMusic = link.url;
            else if (labelLower.includes('deezer') && !acc.deezer) acc.deezer = link.url;
          }
          return acc;
        }, {})
      };

      tracks.push({
        id: trackId,
        title: trackTitle,
        artist,
        src,
        contentId: entry.id || entry.folder || null,
        coverImage: entry.coverImage || (Array.isArray(entry.images) ? entry.images[0] : null),
        createdAt: entry.createdAt || entry.updatedAt || null,
        playlistOrder: tagsObj.playlistOrder || null, // Include playlist order for sorting
        links // Include links for the track
      });
    });
  });

  console.log('🎵 [BUILD-PLAYLIST] Built', tracks.length, 'tracks from content');

  // Sort by playlist order first (if set), then by creation date
  tracks.sort((a, b) => {
    // If both have playlist order, sort by that
    if (a.playlistOrder !== null && b.playlistOrder !== null) {
      return a.playlistOrder - b.playlistOrder;
    }
    // If only one has order, prioritize it
    if (a.playlistOrder !== null) return -1;
    if (b.playlistOrder !== null) return 1;
    // Otherwise sort by creation date
    if (a.createdAt && b.createdAt) {
      return new Date(a.createdAt) - new Date(b.createdAt);
    }
    return 0;
  });

  // Remove playlistOrder from final tracks (not needed in player)
  return tracks.map(({ createdAt, playlistOrder, ...track }) => track);
};

const MusicPlayer = ({ suppressPlayback = false, hidden = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0.3);
  const [currentTrack, setCurrentTrack] = useState(0);
  const [isMinimized, setIsMinimized] = useState(() => {
    const stored = getStoredPlayerUiState();
    return typeof stored.isMinimized === 'boolean' ? stored.isMinimized : true;
  });

  const [isLoading, setIsLoading] = useState(false);
  const [buffered, setBuffered] = useState(0);
  const [showLinksModal, setShowLinksModal] = useState(false);
  const audioRef = useRef(null);
  const savedStateRef = useRef(null);
  const navigationTypeRef = useRef(getNavigationType());
  const skipTrackRestoreRef = useRef(navigationTypeRef.current === 'reload');
  const initialTrackSelectionRef = useRef(false);
  const resumeTimeRef = useRef(0);
  const restoreAttemptRef = useRef(false);
  const lastPersistRef = useRef(0);
  const pendingAutoPlayRef = useRef(false);
  const pendingInteractionResumeRef = useRef(false);
  const scheduledInteractionHandlerRef = useRef(null);
  const initialAutoPlayAttemptedRef = useRef(false);
  const lastKnownPlayingRef = useRef(false);
  const wasSuppressingRef = useRef(false);
  const userInitiatedPauseRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        PLAYER_UI_STORAGE_KEY,
        JSON.stringify({ isMinimized })
      );
    } catch (error) {
      console.warn('🎵 Failed to persist Zee player UI state:', error);
    }
  }, [isMinimized]);

  // Playlist built from managed playlists or content fallback
  const [playlist, setPlaylist] = useState([]);
  const [managedPlaylists, setManagedPlaylists] = useState([]);
  const [activePlaylistId, setActivePlaylistId] = useState(null);
  const [playerEnabled, setPlayerEnabled] = useState(true);

  const playlistService = useRef(new MusicPlaylistService(API_BASE_URL, BUSINESS_ID)).current;

  const tracksFromManagedPlaylist = useCallback((pl) => {
    if (!pl || !Array.isArray(pl.tracks)) return [];
    return pl.tracks.map((t) => ({
      id: t.id || t.mediaAssetId,
      title: t.title || 'Untitled',
      artist: 'Rainbow Sails',
      src: t.audioUrl,
      coverImage: t.coverImage || null,
      productId: t.productId || null,
    }));
  }, []);

  const applyManagedPlaylist = useCallback(
    (playlists, preferredId) => {
      if (!playlists.length) return false;
      const storedId =
        typeof window !== 'undefined' ? window.localStorage.getItem(ACTIVE_PLAYLIST_KEY) : null;
      const pick =
        playlists.find((p) => p.id === preferredId) ||
        playlists.find((p) => p.id === storedId) ||
        playlists.find((p) => p.isDefault) ||
        playlists[0];
      if (!pick) return false;
      const tracks = tracksFromManagedPlaylist(pick);
      if (!tracks.length) return false;
      setManagedPlaylists(playlists);
      setActivePlaylistId(pick.id);
      setPlaylist(tracks);
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(ACTIVE_PLAYLIST_KEY, pick.id);
          window.localStorage.setItem(PLAYLIST_STORAGE_KEY, JSON.stringify(tracks));
        } catch (_) {
          /* ignore */
        }
      }
      return true;
    },
    [tracksFromManagedPlaylist]
  );

  const selectManagedPlaylist = useCallback(
    (playlistId) => {
      const pl = managedPlaylists.find((p) => p.id === playlistId);
      if (!pl) return;
      applyManagedPlaylist(managedPlaylists, playlistId);
      setCurrentTrack(0);
      resumeTimeRef.current = 0;
    },
    [managedPlaylists, applyManagedPlaylist]
  );

  // Check if player is enabled
  const checkPlayerEnabled = useCallback(async () => {
    const enabled = await contentService.getMusicPlayerEnabled();
    setPlayerEnabled(enabled);
  }, []);

  const loadPlaylist = useCallback(async () => {
    // Clear old cached playlist to remove any hardcoded tracks
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(PLAYLIST_STORAGE_KEY);
        console.log('🎵 [MUSIC-PLAYER] Cleared old playlist cache');
      } catch (error) {
        console.warn('🎵 Failed to clear playlist cache:', error);
      }
    }

    try {
      console.log('🎵 [MUSIC-PLAYER] Loading managed playlists...');
      const managed = await playlistService.fetchPublicPlaylists();
      if (managed.length > 0 && applyManagedPlaylist(managed)) {
        console.log('🎵 [MUSIC-PLAYER] ✅ Using managed playlists');
        return;
      }
    } catch (playlistErr) {
      console.warn('🎵 [MUSIC-PLAYER] Managed playlists unavailable:', playlistErr);
    }

    try {
      console.log('🎵 [MUSIC-PLAYER] Loading playlist from content service...');
      const content = await contentService.getContent();
      console.log('🎵 [MUSIC-PLAYER] Content received:', {
        count: content.length,
        items: content.map(c => ({
          id: c.id,
          title: c.title || c.name,
          contentType: c.contentType,
          hasTags: !!c.tags,
          tagsType: typeof c.tags,
          tagsValue: c.tags,
          hasAudioUrl: !!(c.tags && c.tags.audioUrl)
        }))
      });
      
      const contentPlaylist = buildPlaylistFromContent(content);
      console.log('🎵 [MUSIC-PLAYER] Playlist built from content:', {
        trackCount: contentPlaylist.length,
        tracks: contentPlaylist.map(t => ({ id: t.id, title: t.title, src: t.src }))
      });

      if (contentPlaylist.length > 0) {
        console.log('🎵 [MUSIC-PLAYER] ✅ Using content playlist with', contentPlaylist.length, 'tracks');
        restoreAttemptRef.current = false;
        initialTrackSelectionRef.current = false;
        setPlaylist(contentPlaylist);

        // Only cache if we successfully got tracks from API
        if (typeof window !== 'undefined') {
          try {
            window.localStorage.setItem(PLAYLIST_STORAGE_KEY, JSON.stringify(contentPlaylist));
            console.log('🎵 [MUSIC-PLAYER] Playlist cached to localStorage');
          } catch (storageError) {
            console.warn('🎵 Failed to cache Zee content playlist:', storageError);
          }
        }
        return;
      } else {
        console.log('🎵 [MUSIC-PLAYER] ⚠️ No music tracks found in content');
        
        // Check if there's music content but without audio URLs
        const musicContentWithoutAudio = content.filter(c => 
          (c.contentType === 'music' || isMusicContentEntry(c)) && 
          !(c.tags && c.tags.audioUrl)
        );
        
        if (musicContentWithoutAudio.length > 0) {
          console.warn('🎵 [MUSIC-PLAYER] ⚠️ Found music content but missing audio URLs:', {
            count: musicContentWithoutAudio.length,
            items: musicContentWithoutAudio.map(c => ({
              id: c.id,
              title: c.title || c.name,
              contentType: c.contentType,
              hasTags: !!c.tags
            }))
          });
        }
      }
    } catch (contentError) {
      console.error('🎵 Could not build playlist from content:', contentError);
      console.error('🎵 [MUSIC-PLAYER] ❌ Failed to load playlist from API - player will be empty');
    }

    // No fallback to localStorage - only load from music manager
    console.log('🎵 [MUSIC-PLAYER] ⚠️ No playlist available - only loading from music manager');
    restoreAttemptRef.current = false;
    setPlaylist([]);
  }, [applyManagedPlaylist, playlistService]);

  useEffect(() => {
    checkPlayerEnabled();
    loadPlaylist();
    
    const handlePlaylistUpdate = () => {
      loadPlaylist();
    };
    
    if (typeof window !== 'undefined') {
    window.addEventListener('playlistUpdated', handlePlaylistUpdate);
    return () => window.removeEventListener('playlistUpdated', handlePlaylistUpdate);
    }

    return undefined;
  }, [loadPlaylist, checkPlayerEnabled]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const rawState = window.localStorage.getItem(PLAYER_STATE_STORAGE_KEY) || '{}';
      const savedState = JSON.parse(rawState);

      if (savedState && typeof savedState === 'object') {
        if (typeof savedState.volume === 'number') {
          setVolume(clampVolume(savedState.volume));
        }

        if (typeof savedState.isMuted === 'boolean') {
          setIsMuted(savedState.isMuted);
        }

        if (typeof savedState.userInitiatedPause === 'boolean') {
          userInitiatedPauseRef.current = savedState.userInitiatedPause;
          if (savedState.userInitiatedPause) {
            initialAutoPlayAttemptedRef.current = true;
          }
        }

        const shouldRestorePlaybackState = !skipTrackRestoreRef.current && !!(savedState.trackId || savedState.src);

        if (shouldRestorePlaybackState) {
          savedStateRef.current = savedState;
          restoreAttemptRef.current = false;
          resumeTimeRef.current = Math.max(0, savedState.currentTime || 0);

          if (typeof savedState.isPlaying === 'boolean') {
            pendingAutoPlayRef.current = savedState.isPlaying;
            if (!suppressPlayback) {
              setIsPlaying(savedState.isPlaying);
            }
            if (savedState.isPlaying) {
              userInitiatedPauseRef.current = false;
            }
          }
        } else {
          savedStateRef.current = null;
          pendingAutoPlayRef.current = false;
          if (userInitiatedPauseRef.current && !suppressPlayback) {
            setIsPlaying(false);
          }
        }

        if (userInitiatedPauseRef.current) {
          pendingAutoPlayRef.current = false;
        }
      }
    } catch (error) {
      console.warn('🎵 Failed to restore Zee player state:', error);
    }
  }, [suppressPlayback]);

  const currentSong = playlist[currentTrack] || null;

  useEffect(() => {
    if (!playlist.length) {
      resumeTimeRef.current = 0;
      initialTrackSelectionRef.current = false;
      return;
    }

    if (restoreAttemptRef.current) {
      if (currentTrack >= playlist.length) {
        setCurrentTrack(0);
        resumeTimeRef.current = 0;
      }
      return;
    }

    restoreAttemptRef.current = true;

    const savedState = savedStateRef.current;
    if (savedState) {
      const matchedIndex = playlist.findIndex(
        (track) => track.id === savedState.trackId || track.src === savedState.src
      );

      if (matchedIndex !== -1) {
        setCurrentTrack(matchedIndex);
        resumeTimeRef.current = Math.max(0, savedState.currentTime || 0);
        const shouldResume = !suppressPlayback && !!savedState.isPlaying;
        pendingAutoPlayRef.current = shouldResume;
        setIsPlaying(shouldResume);
        if (shouldResume) {
          userInitiatedPauseRef.current = false;
        }
        savedStateRef.current = null;
        initialTrackSelectionRef.current = true;
        skipTrackRestoreRef.current = false;
        return;
      }
    }

    if (!initialTrackSelectionRef.current) {
      const randomIndex = Math.floor(Math.random() * playlist.length);
      initialTrackSelectionRef.current = true;
      skipTrackRestoreRef.current = false;
      resumeTimeRef.current = 0;
      savedStateRef.current = null;
      pendingAutoPlayRef.current = false;
      setCurrentTrack(randomIndex);
      setIsPlaying(false);
      return;
    }

    resumeTimeRef.current = 0;
    skipTrackRestoreRef.current = false;
    pendingAutoPlayRef.current = false;
    setCurrentTrack(0);
    setIsPlaying(false);
  }, [playlist, currentTrack, suppressPlayback]);

  useEffect(() => {
    if (suppressPlayback) return;
    if (playlist.length > 0 && !initialAutoPlayAttemptedRef.current) {
      initialAutoPlayAttemptedRef.current = true;

      if (userInitiatedPauseRef.current) {
        pendingAutoPlayRef.current = false;
        setIsPlaying(false);
        return;
      }

      if (isAutoPlayAllowed()) {
        pendingAutoPlayRef.current = true;
        userInitiatedPauseRef.current = false;
        setIsPlaying(true);
      } else {
        pendingAutoPlayRef.current = false;
        userInitiatedPauseRef.current = false;
        setIsPlaying(false);
      }
    }
  }, [playlist.length, suppressPlayback]);

  const savePlayerState = useCallback(
    (overrides = {}) => {
      if (typeof window === 'undefined') return;
      const audio = audioRef.current;
      const track = playlist[currentTrack] || null;

      const state = {
        trackId: track?.id || null,
        src: track?.src || null,
        trackIndex: currentTrack,
        currentTime: overrides.currentTime ?? (audio ? audio.currentTime : 0) ?? 0,
        isPlaying: overrides.isPlaying ?? isPlaying,
        volume: overrides.volume ?? volume,
        isMuted: overrides.isMuted ?? isMuted,
        userInitiatedPause: overrides.userInitiatedPause ?? userInitiatedPauseRef.current
      };

      try {
        window.localStorage.setItem(PLAYER_STATE_STORAGE_KEY, JSON.stringify(state));
      } catch (error) {
        console.warn('🎵 Failed to persist Zee player state:', error);
      }
    },
    [playlist, currentTrack, isPlaying, volume, isMuted]
  );

  const clearInteractionHandler = useCallback(() => {
    if (typeof document === 'undefined') return;
    if (scheduledInteractionHandlerRef.current) {
      document.removeEventListener('pointerdown', scheduledInteractionHandlerRef.current);
      document.removeEventListener('keydown', scheduledInteractionHandlerRef.current);
      scheduledInteractionHandlerRef.current = null;
    }
    pendingInteractionResumeRef.current = false;
  }, []);

  const scheduleResumeOnInteraction = useCallback(() => {
    if (suppressPlayback) return;
    if (typeof document === 'undefined') return;
    if (pendingInteractionResumeRef.current) return;

    pendingInteractionResumeRef.current = true;

    const handler = () => {
      document.removeEventListener('keydown', handler);
      clearInteractionHandler();

      const audio = audioRef.current;
      if (!audio) {
        return;
      }

      audio
        .play()
        .then(() => {
          setIsPlaying(true);
          setIsLoading(false);
          pendingAutoPlayRef.current = false;
          userInitiatedPauseRef.current = false;
          setLastAutoPlayTimestamp(Date.now());
          savePlayerState({ isPlaying: true, userInitiatedPause: false });
        })
        .catch((error) => {
          console.warn('🎵 Zee auto-play still blocked after interaction:', error);
        });
    };

    scheduledInteractionHandlerRef.current = handler;
    document.addEventListener('pointerdown', handler, { once: true });
    document.addEventListener('keydown', handler, { once: true });
  }, [clearInteractionHandler, savePlayerState, suppressPlayback]);

  useEffect(() => {
    return () => {
      clearInteractionHandler();
    };
  }, [clearInteractionHandler]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      if (!suppressPlayback) {
        wasSuppressingRef.current = false;
      }
      lastKnownPlayingRef.current = false;
      return;
    }

    if (suppressPlayback) {
      if (!wasSuppressingRef.current) {
        wasSuppressingRef.current = true;
        lastKnownPlayingRef.current = !audio.paused || isPlaying || pendingAutoPlayRef.current;
      }
      pendingAutoPlayRef.current = false;
      initialAutoPlayAttemptedRef.current = false;

      if (!audio.paused) {
        audio.pause();
      }

      if (isPlaying) {
        setIsPlaying(false);
      }

      userInitiatedPauseRef.current = false;
      savePlayerState({ isPlaying: false, currentTime: audio.currentTime, userInitiatedPause: false });
      clearInteractionHandler();
      return;
    }

    if (!wasSuppressingRef.current) {
      return;
    }

    wasSuppressingRef.current = false;

    const shouldResume = lastKnownPlayingRef.current;
    lastKnownPlayingRef.current = false;

    if (!shouldResume) {
      savePlayerState({ currentTime: audio.currentTime, isPlaying: false, userInitiatedPause: userInitiatedPauseRef.current });
      return;
    }

    const attemptResume = () => {
      pendingAutoPlayRef.current = false;
      setIsLoading(false);

      audio
        .play()
        .then(() => {
          setIsPlaying(true);
          setIsLoading(false);
          clearInteractionHandler();
          userInitiatedPauseRef.current = false;
          savePlayerState({ isPlaying: true, userInitiatedPause: false });
        })
        .catch((error) => {
          setIsLoading(false);
          if (error?.name === 'NotAllowedError' || error?.name === 'NotSupportedError') {
            pendingAutoPlayRef.current = true;
            setIsPlaying(false);
            scheduleResumeOnInteraction();
          } else {
            console.warn('🎵 Zee playback resume failed after suppression:', error);
            pendingAutoPlayRef.current = false;
            setIsPlaying(false);
          }
          savePlayerState({ isPlaying: false, userInitiatedPause: userInitiatedPauseRef.current });
        });
    };

    attemptResume();
  }, [suppressPlayback, isPlaying, savePlayerState, clearInteractionHandler, scheduleResumeOnInteraction]);

  const togglePlayer = useCallback(() => {
    setIsMinimized(prev => !prev);
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    audio.volume = clampVolume(volume);
    audio.muted = isMuted;
      audio.preload = 'metadata'; // Load metadata only initially
      
      const handleEnded = () => {
      if (!playlist.length) return;
      resumeTimeRef.current = 0;
      setCurrentTrack((prev) => {
        if (!playlist.length) return prev;
        const nextIndex = (prev + 1) % playlist.length;
        return nextIndex;
      });
      savePlayerState({ currentTime: 0 });
      };

      const handleLoadStart = () => {
        setIsLoading(true);
      };

      const handleCanPlay = () => {
        setIsLoading(false);
      };

      const handleWaiting = () => {
        setIsLoading(true);
      };

      const handleCanPlayThrough = () => {
        setIsLoading(false);
      };

      const handleProgress = () => {
        if (audio.buffered.length > 0) {
          const bufferedEnd = audio.buffered.end(audio.buffered.length - 1);
          const duration = audio.duration;
          if (duration > 0) {
            setBuffered((bufferedEnd / duration) * 100);
          }
        }
      };

    const handleTimeUpdate = () => {
      const now = Date.now();
      if (now - lastPersistRef.current > 2000) {
        lastPersistRef.current = now;
        savePlayerState();
      }
    };

    const handlePause = () => {
      lastPersistRef.current = Date.now();
      savePlayerState();
    };

    const handlePlay = () => {
      lastPersistRef.current = Date.now();
      savePlayerState();
      };

      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('loadstart', handleLoadStart);
      audio.addEventListener('canplay', handleCanPlay);
      audio.addEventListener('waiting', handleWaiting);
      audio.addEventListener('canplaythrough', handleCanPlayThrough);
      audio.addEventListener('progress', handleProgress);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('play', handlePlay);
      
      return () => {
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('loadstart', handleLoadStart);
        audio.removeEventListener('canplay', handleCanPlay);
        audio.removeEventListener('waiting', handleWaiting);
        audio.removeEventListener('canplaythrough', handleCanPlayThrough);
        audio.removeEventListener('progress', handleProgress);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('play', handlePlay);
      };
  }, [playlist.length, volume, isMuted, savePlayerState]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentSong?.src) {
      return;
    }

      setIsLoading(true);
      audio.src = currentSong.src;
      audio.load(); // Force reload the new source
      
    const handleLoadedData = () => {
      if (suppressPlayback) {
        setIsLoading(false);
        userInitiatedPauseRef.current = false;
        savePlayerState({ isPlaying: false, userInitiatedPause: false });
        return;
      }

      try {
        if (resumeTimeRef.current > 0) {
          audio.currentTime = resumeTimeRef.current;
        }
      } catch (seekError) {
        console.warn('🎵 Failed to restore Zee track position:', seekError);
      } finally {
        resumeTimeRef.current = 0;
      }

      const shouldAutoPlay = (isPlaying || pendingAutoPlayRef.current) && !suppressPlayback;

      if (shouldAutoPlay) {
        const wasPendingAutoPlay = pendingAutoPlayRef.current;
        audio
          .play()
          .then(() => {
            setIsPlaying(true);
            setIsLoading(false);
            pendingAutoPlayRef.current = false;
            clearInteractionHandler();
            userInitiatedPauseRef.current = false;
            if (wasPendingAutoPlay) {
              setLastAutoPlayTimestamp(Date.now());
            }
            savePlayerState({ isPlaying: true, userInitiatedPause: false });
          })
          .catch((playError) => {
            if (playError?.name === 'NotAllowedError' || playError?.name === 'NotSupportedError') {
              pendingAutoPlayRef.current = true;
              setIsPlaying(false);
              setIsLoading(false);
              savePlayerState({ isPlaying: false, userInitiatedPause: userInitiatedPauseRef.current });
              scheduleResumeOnInteraction();
            } else {
              console.error('🎵 Zee player could not auto-play:', playError);
              pendingAutoPlayRef.current = false;
              setIsPlaying(false);
        setIsLoading(false);
              savePlayerState({ isPlaying: false, userInitiatedPause: userInitiatedPauseRef.current });
            }
          });
        return;
      }

      setIsLoading(false);
      savePlayerState();
    };

    audio.addEventListener('loadeddata', handleLoadedData, { once: true });

    return () => {
      audio.removeEventListener('loadeddata', handleLoadedData);
    };
  }, [currentSong, isPlaying, savePlayerState, clearInteractionHandler, scheduleResumeOnInteraction, suppressPlayback]);

  useEffect(() => {
    return () => {
      savePlayerState();
    };
  }, [savePlayerState]);

  const togglePlay = () => {
    if (suppressPlayback) return;
    const audio = audioRef.current;
    if (!audio || !currentSong?.src) {
      return;
    }

    if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      pendingAutoPlayRef.current = false;
      lastPersistRef.current = Date.now();
      userInitiatedPauseRef.current = true;
      savePlayerState({ isPlaying: false, userInitiatedPause: true });
      return;
    }

    const attemptPlay = () => {
      pendingAutoPlayRef.current = false;
      clearInteractionHandler();
      audio
        .play()
        .then(() => {
          setIsPlaying(true);
          setIsLoading(false);
          lastPersistRef.current = Date.now();
          userInitiatedPauseRef.current = false;
          savePlayerState({ isPlaying: true, userInitiatedPause: false });
        })
        .catch((error) => {
          if (error?.name === 'NotAllowedError' || error?.name === 'NotSupportedError') {
            scheduleResumeOnInteraction();
          } else {
            console.error('🎵 Zee playback failed:', error);
          }
          setIsPlaying(false);
          setIsLoading(false);
          savePlayerState({ isPlaying: false });
        });
    };

    if (audio.readyState >= 2) {
      attemptPlay();
        } else {
          setIsLoading(true);
      const handleCanPlay = () => {
        audio.removeEventListener('canplay', handleCanPlay);
        attemptPlay();
      };
      audio.addEventListener('canplay', handleCanPlay, { once: true });
      audio.load();
    }
  };

  const toggleMute = () => {
    if (suppressPlayback) return;
    const audio = audioRef.current;
    const nextMuted = !isMuted;
    setIsMuted(nextMuted);
    if (audio) {
      audio.muted = nextMuted;
    }
    savePlayerState({ isMuted: nextMuted });
  };

  const handleVolumeChange = (e) => {
    if (suppressPlayback) return;
    const newVolume = clampVolume(parseFloat(e.target.value));
    setVolume(newVolume);
    if (audioRef.current && !Number.isNaN(newVolume)) {
      audioRef.current.volume = newVolume;
    }
    savePlayerState({ volume: newVolume });
  };

  const nextTrack = () => {
    if (playlist.length > 0) {
      resumeTimeRef.current = 0;
      setCurrentTrack((prev) => (prev + 1) % playlist.length);
    }
  };

  const prevTrack = () => {
    if (playlist.length > 0) {
      resumeTimeRef.current = 0;
      setCurrentTrack((prev) => (prev - 1 + playlist.length) % playlist.length);
    }
  };

  // Don't render if player is disabled or no playlist - completely hide the player
  if (!playerEnabled || playlist.length === 0) {
    return null;
  }

  return (
    <div className="music-player-container" style={{
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      zIndex: 9998,
      background: 'rgba(31, 41, 55, 0.95)',
      backdropFilter: 'blur(10px)',
      borderRadius: isMinimized ? '50%' : '16px',
      border: '2px solid rgba(139, 92, 246, 0.5)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
      transition: 'all 0.3s ease',
      width: isMinimized ? '60px' : '300px',
      height: isMinimized ? '60px' : 'auto',
      overflow: 'hidden',
      display: hidden ? 'none' : undefined,
      pointerEvents: hidden ? 'none' : undefined
    }}>
      <audio ref={audioRef} />
      
      {isMinimized ? (
        // Minimized View - Toggle button
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <button
            onClick={togglePlayer}
            style={{
              background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white'
            }}
            title="Open Music Player"
          >
            {isPlaying ? '🎵' : '🎶'}
          </button>
        </div>
      ) : (
        // Full Player View
        <div style={{ padding: '16px' }}>
          {/* Header with toggle and link buttons */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{
              color: 'white',
              fontSize: '14px',
              fontWeight: 'bold',
              margin: 0,
              background: 'linear-gradient(45deg, #ec4899, #8b5cf6, #06b6d4)',
              backgroundSize: '200% 200%',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: 'gradientShift 3s ease-in-out infinite'
            }}>
              🎵 Rainbow Vibes
            </h3>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {/* Link Button */}
              {currentSong?.links && Object.values(currentSong.links).some(link => link) && (
                <button
                  onClick={() => setShowLinksModal(true)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#9ca3af',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#ec4899'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
                  title="View Links"
                >
                  <Link size={16} />
                </button>
              )}
              {/* Toggle Button */}
              <button
                onClick={togglePlayer}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#ec4899'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
                title="Minimize Player"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {managedPlaylists.length > 1 && (
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: 11, color: '#9ca3af', marginBottom: 4 }}>
                Playlist
              </label>
              <select
                value={activePlaylistId || ''}
                onChange={(e) => selectManagedPlaylist(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  borderRadius: 6,
                  border: '1px solid #4b5563',
                  background: '#1f2937',
                  color: '#fff',
                  fontSize: 13,
                }}
              >
                {managedPlaylists.map((pl) => (
                  <option key={pl.id} value={pl.id}>
                    {pl.name} ({pl.tracks?.length ?? 0})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Track Info */}
          <div style={{ marginBottom: '12px', textAlign: 'center' }}>
            <p style={{ color: 'white', fontSize: '13px', fontWeight: '500', margin: 0 }}>
              {currentSong?.title || 'No Track'}
            </p>
            <p style={{ color: '#9ca3af', fontSize: '11px', margin: 0 }}>
              by {currentSong?.artist || 'Unknown'}
            </p>
          </div>

          {/* Controls */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '12px' }}>
            <button
              onClick={prevTrack}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#9ca3af',
                cursor: 'pointer',
                padding: '4px'
              }}
            >
              <SkipBack size={16} />
            </button>
            
            <button
              onClick={togglePlay}
              disabled={isLoading}
              style={{
                background: isLoading ? '#6b7280' : 'linear-gradient(135deg, #ec4899, #8b5cf6)',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                color: 'white',
                transition: 'transform 0.2s ease',
                opacity: isLoading ? 0.7 : 1
              }}
              onMouseEnter={(e) => !isLoading && (e.target.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              {isLoading ? (
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
              ) : isPlaying ? <Pause size={16} /> : <Play size={16} />}
            </button>
            
            <button
              onClick={nextTrack}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#9ca3af',
                cursor: 'pointer',
                padding: '4px'
              }}
            >
              <SkipForward size={16} />
            </button>
          </div>

          {/* Volume Control */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={toggleMute}
              style={{
                background: 'transparent',
                border: 'none',
                color: isMuted ? '#ef4444' : '#9ca3af',
                cursor: 'pointer',
                padding: '4px'
              }}
            >
              {isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            </button>
            
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
              style={{
                flex: 1,
                height: '4px',
                background: '#374151',
                borderRadius: '2px',
                outline: 'none',
                cursor: 'pointer'
              }}
            />
          </div>

          {/* Buffering Progress */}
          {isLoading && (
            <div style={{ marginTop: '8px' }}>
              <div style={{
                fontSize: '10px',
                color: '#9ca3af',
                textAlign: 'center',
                marginBottom: '4px'
              }}>
                Buffering...
              </div>
              <div style={{
                width: '100%',
                height: '3px',
                background: '#374151',
                borderRadius: '2px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${buffered}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #ec4899, #8b5cf6)',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>
          )}

          {/* Track Progress */}
          <div style={{
            fontSize: '10px',
            color: '#6b7280',
            textAlign: 'center',
            marginTop: '8px'
          }}>
            Track {currentTrack + 1} of {playlist.length}
          </div>
        </div>
      )}

      {/* CSS Animation */}
      <style>{`
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* Links Modal */}
      {showLinksModal && currentSong?.links && (
        <div
          onClick={() => setShowLinksModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            backdropFilter: 'blur(4px)'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'rgba(31, 41, 55, 0.98)',
              backdropFilter: 'blur(20px)',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              border: '2px solid rgba(139, 92, 246, 0.5)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: 'white', margin: 0, fontSize: '18px', fontWeight: 'bold' }}>
                {currentSong.title}
              </h3>
              <button
                onClick={() => setShowLinksModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#ec4899'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Website Link (First) */}
              {currentSong.links.website && (
                <a
                  href={currentSong.links.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    background: 'rgba(139, 92, 246, 0.2)',
                    borderRadius: '8px',
                    color: 'white',
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                    border: '1px solid rgba(139, 92, 246, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(139, 92, 246, 0.3)';
                    e.currentTarget.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(139, 92, 246, 0.2)';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <span style={{ fontSize: '20px' }}>🌐</span>
                  <span style={{ flex: 1, fontWeight: '500' }}>View on Website</span>
                  <Link size={16} style={{ opacity: 0.7 }} />
                </a>
              )}

              {/* Spotify */}
              {currentSong.links.spotify && (
                <a
                  href={currentSong.links.spotify}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    background: 'rgba(30, 215, 96, 0.2)',
                    borderRadius: '8px',
                    color: 'white',
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                    border: '1px solid rgba(30, 215, 96, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(30, 215, 96, 0.3)';
                    e.currentTarget.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(30, 215, 96, 0.2)';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <span style={{ fontSize: '20px' }}>🎵</span>
                  <span style={{ flex: 1, fontWeight: '500' }}>Open on Spotify</span>
                  <Link size={16} style={{ opacity: 0.7 }} />
                </a>
              )}

              {/* YouTube */}
              {currentSong.links.youtube && (
                <a
                  href={currentSong.links.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    background: 'rgba(255, 0, 0, 0.2)',
                    borderRadius: '8px',
                    color: 'white',
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                    border: '1px solid rgba(255, 0, 0, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 0, 0, 0.3)';
                    e.currentTarget.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 0, 0, 0.2)';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <span style={{ fontSize: '20px' }}>▶️</span>
                  <span style={{ flex: 1, fontWeight: '500' }}>Watch on YouTube</span>
                  <Link size={16} style={{ opacity: 0.7 }} />
                </a>
              )}

              {/* Apple Music */}
              {currentSong.links.appleMusic && (
                <a
                  href={currentSong.links.appleMusic}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    background: 'rgba(255, 45, 85, 0.2)',
                    borderRadius: '8px',
                    color: 'white',
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                    border: '1px solid rgba(255, 45, 85, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 45, 85, 0.3)';
                    e.currentTarget.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 45, 85, 0.2)';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <span style={{ fontSize: '20px' }}>🍎</span>
                  <span style={{ flex: 1, fontWeight: '500' }}>Open on Apple Music</span>
                  <Link size={16} style={{ opacity: 0.7 }} />
                </a>
              )}

              {/* Amazon Music */}
              {currentSong.links.amazonMusic && (
                <a
                  href={currentSong.links.amazonMusic}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    background: 'rgba(255, 153, 0, 0.2)',
                    borderRadius: '8px',
                    color: 'white',
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                    border: '1px solid rgba(255, 153, 0, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 153, 0, 0.3)';
                    e.currentTarget.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 153, 0, 0.2)';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <span style={{ fontSize: '20px' }}>🎧</span>
                  <span style={{ flex: 1, fontWeight: '500' }}>Open on Amazon Music</span>
                  <Link size={16} style={{ opacity: 0.7 }} />
                </a>
              )}

              {/* Deezer */}
              {currentSong.links.deezer && (
                <a
                  href={currentSong.links.deezer}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    background: 'rgba(177, 70, 194, 0.2)',
                    borderRadius: '8px',
                    color: 'white',
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                    border: '1px solid rgba(177, 70, 194, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(177, 70, 194, 0.3)';
                    e.currentTarget.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(177, 70, 194, 0.2)';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <span style={{ fontSize: '20px' }}>💜</span>
                  <span style={{ flex: 1, fontWeight: '500' }}>Open on Deezer</span>
                  <Link size={16} style={{ opacity: 0.7 }} />
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MusicPlayer;
